// Libraries
import styled from 'styled-components'

// Custom
import {
  CarouselLeft,
  CarouselRight,
  FloatDownUp,
  FloatSlightly,
  FloatUpDown,
  GhostFloat,
  LogoLeft,
  LogoLeftShade,
  LogoRight,
  LogoRightShade,
  Path,
  Pop1,
  Pop2,
  PopBig,
  Shining,
  ShiningShadow,
} from 'utils/animations'
import { LightButtonLink, MainButtonLink } from 'components/UI/Buttons'

// Assets
import DeskTop from 'images/images/TestimonialBackgroundDeskTop.png'
import DeskBottom from 'images/images/TestimonialBackgroundDeskBottom.png'
import MobileTop from 'images/images/TestimonialBackgroundMobileTop.png'
import MobileBottom from 'images/images/TestimonialBackgroundMobileBottom.png'
import ProjectBackground from 'images/images/projectSectionMobileBackground.png'

type ImgProps = {
  animation?: boolean
  animationType?: string
  animationUp?: boolean
  permanent?: boolean
  right?: string
  rotate?: number
  top?: string
  startAnimation?: boolean
}

type CarouselProps = {
  active?: boolean
}

export const Wrapper = styled.div`
  width: 100%;
`

export const IntroSection = styled.div`
  width: 100%;
`

export const IntroSectionTop = styled.div`
  display: flex;
  flex-direction: column;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    flex-direction: row;
  }
`

export const TitlesContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: center;
  align-items: center;
  padding: 40px 0px 80px 0px;
  ${MainButtonLink} {
    width: 244px;
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    max-width: 440px;
    padding: 127px 0px 80px 0px;
    align-items: flex-start;
    ${MainButtonLink} {
      margin-top: 24px;
    }
  }
`

export const Title = styled.h1`
  margin: 0px 0px 12px 0px;
  color: ${({ theme }) => theme.colors.blueDarkerDefault};
  font-family: ${({ theme }) => theme.fontFamily.lato};
  font-weight: ${({ theme }) => theme.fontWeight.extraBold};
  font-size: ${({ theme }) => theme.fontSize.h4};
  line-height: 33px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    text-align: left;
    line-height: 57px;
    font-size: ${({ theme }) => theme.fontSize.h2};
  }
`

export const Text = styled.p`
  margin: 0px 0px 16px 0px;
  color: ${({ theme }) => theme.colors.blueDarkerLight};
  font-family: ${({ theme }) => theme.fontFamily.lato};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  font-size: ${({ theme }) => theme.fontSize.h6};
  line-height: 21px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    text-align: left;
    font-size: ${({ theme }) => theme.fontSize.p};
  }
`

export const LogoAnimationContainer = styled.div`
  display: none;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    position: relative;
    display: block;
    height: 537px;
    width: 60%;
  }
`

export const ImageAnimation = styled.img<ImgProps>`
  position: absolute;
  top: ${({ top }) => top || 0}px;
  right: calc(${({ right }) => right || 0}px - 114px);
  animation-name: ${({ animationType, permanent, animationUp }) =>
    permanent
      ? animationUp
        ? FloatUpDown
        : FloatDownUp
      : animationType === 'leftLogo'
      ? LogoLeft
      : animationType === 'leftLogoShade'
      ? LogoLeftShade
      : animationType === 'rightLogo'
      ? LogoRight
      : animationType === 'rightLogoShade'
      ? LogoRightShade
      : animationType === 'pop1'
      ? Pop1
      : animationType === 'pop2'
      ? Pop2
      : PopBig};
  animation-duration: ${({ permanent }) => (permanent ? 18 : 2)}s;
  animation-iteration-count: ${({ permanent }) =>
    permanent ? 'infinite' : 'inherit'};
`

export const IntroCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    background: ${({ theme }) => theme.colors.white00};
    box-shadow: 0px 0px 40px rgba(122, 119, 134, 0.1);
    padding: 48px 62px;
    margin-bottom: 121px;
  }
`

export const DotLine = styled.img`
  display: none;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    display: block;
    margin-left: 90px;
  }
`

export const IntroCard = styled.div`
  position: relative;
  display: flex;
  margin-bottom: 48px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    margin-bottom: 0px;
    :last-child {
      ${DotLine} {
        display: none;
      }
    }
  }
`

export const IconContainer = styled.div`
  height: 42px;
  width: 42px;
  border-radius: 100px;
  margin-right: 22px;
  background-color: ${({ theme }) => theme.colors.blue800};
  display: flex;
  align-items: center;
  justify-content: center;
`

export const Icon = styled.img`
  object-fit: fill;
`

export const IntroCardTitlesContainer = styled.div`
  position: relative;
  img {
    position: absolute;
    top: 6px;
    left: 100%;
  }
`

export const IntroCardTitle = styled.div`
  margin: 0px;
  color: ${({ theme }) => theme.colors.gray02};
  font-family: ${({ theme }) => theme.fontFamily.lato};
  font-weight: ${({ theme }) => theme.fontWeight.semiBold};
  font-size: ${({ theme }) => theme.fontSize.p};
  line-height: 18px;
`

export const IntroCardText = styled.div`
  margin: 0px;
  color: ${({ theme }) => theme.colors.gray04};
  font-family: ${({ theme }) => theme.fontFamily.lato};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  font-size: ${({ theme }) => theme.fontSize.small};
  line-height: 18px;
`

export const ConceptionSection = styled.div`
  text-align: center;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    text-align: left;
    margin-bottom: 227px;
  }
`

export const ConceptionTextContainer = styled.div`
  max-width: 398px;
`

export const ConceptionAnimation = styled.img<ImgProps>`
  position: absolute;
  top: ${({ top }) => top || 0}px;
  right: calc(${({ right }) => right || 0}px - 114px);
  animation: ${({ animation }) => animation && FloatSlightly} 10s ease-in-out
    infinite;
  animation-fill-mode: forwards;
`

export const ConceptionAnimationContainer = styled.div<ImgProps>`
  position: relative;
  width: 100%;
  @media (prefers-reduced-motion: no-preference) {
    ${ConceptionAnimation}:nth-child(2) {
      opacity: 0;
      animation: ${({ startAnimation })=> startAnimation && Path} 0.3s ease-in-out forwards;
    }
    ${ConceptionAnimation}:nth-child(4) {
      opacity: 0;
      animation: ${({ startAnimation })=> startAnimation && Path} 0.3s ease-in-out 0.3s forwards;
    }
    ${ConceptionAnimation}:nth-child(8) {
      opacity: 0;
      animation: ${({ startAnimation })=> startAnimation && Path} 0.3s ease-in-out 0.6s forwards;
    }
    ${ConceptionAnimation}:nth-child(6) {
      opacity: 0;
      animation: ${({ startAnimation })=> startAnimation && Path} 0.3s ease-in-out 0.9s forwards;
    }
    ${ConceptionAnimation}:nth-child(10) {
      opacity: 0;
      animation: ${({ startAnimation })=> startAnimation && Path} 0.3s ease-in-out 1.2s forwards;
    }
    ${ConceptionAnimation}:nth-child(12) {
      opacity: 0;
      animation: ${({ startAnimation })=> startAnimation && Path} 0.3s ease-in-out 1.5s forwards;
    }
  }
`

export const ConceptionTitle = styled.h1`
  margin: 64px 0px 44px 0px;
  color: ${({ theme }) => theme.colors.blueDarkerDefault};
  font-family: ${({ theme }) => theme.fontFamily.lato};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  font-size: ${({ theme }) => theme.fontSize.h5};
  line-height: 36px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    margin: 14px 0px 16px 0px;
    font-weight: ${({ theme }) => theme.fontWeight.black};
    font-size: ${({ theme }) => theme.fontSize.h4};
    line-height: 44px;
  }
`

export const ConceptionSubTitle = styled.h2`
  margin: 0px;
  color: ${({ theme }) => theme.colors.gray02};
  font-family: ${({ theme }) => theme.fontFamily.lato};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  font-size: ${({ theme }) => theme.fontSize.small};
  line-height: 24px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    color: ${({ theme }) => theme.colors.gray04};
    font-family: ${({ theme }) => theme.fontFamily.inter};
    font-weight: ${({ theme }) => theme.fontWeight.medium};
    font-size: ${({ theme }) => theme.fontSize.p};
  }
`

export const ConceptionText = styled.p`
  margin: 8px 0px 0px 0px;
  color: ${({ theme }) => theme.colors.gray04};
  font-family: ${({ theme }) => theme.fontFamily.inter};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  font-size: ${({ theme }) => theme.fontSize.xsmall};
  line-height: 21px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    color: ${({ theme }) => theme.colors.gray03};
    font-size: ${({ theme }) => theme.fontSize.small};
    line-height: 21px;
  }
`

export const ConceptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 48px;
  justify-content: space-between;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    margin-top: 30px;
  }
`

export const ConceptionsDiv = styled.div`
  width: 50%;
  display: flex;
  justify-content: flex-start;
`

export const ConceptionsIcon = styled.img`
  margin-right: 40px;
`

export const ConceptionsTitle = styled.h3`
  color: ${({ theme }) => theme.colors.blueDarkerLight};
  font-family: ${({ theme }) => theme.fontFamily.lato};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  font-size: ${({ theme }) => theme.fontSize.xsmall};
  line-height: 21px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    font-size: ${({ theme }) => theme.fontSize.small};
  }
`

export const ProjectSection = styled.div`
  position: relative;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.blueDarkerLight};
  padding: 67px 0px;
  background-image: url(${ProjectBackground});
  background-repeat: no-repeat;
  background-size: 100%;
  margin-top: 96px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    background-size: 0px;
    padding: 183px 0px;
    display: flex;
    justify-content: space-between;
  }
`

export const ProjectSectionTextContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  ${LightButtonLink}{
    min-width: 195px;
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    max-width: 305px;
    padding-top: 71px;
    align-items: flex-start;
  }
`

export const ProjectSectionTitle = styled.h1`
  margin: 0px 0px 24px 0px;
  text-align: center;
  color: ${({ theme }) => theme.colors.white00};
  font-family: ${({ theme }) => theme.fontFamily.lato};
  font-weight: ${({ theme }) => theme.fontWeight.extraBold};
  font-size: ${({ theme }) => theme.fontSize.h5};
  line-height: 28px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    text-align: left;
  }
`

export const ProjectSectionText = styled.p`
  margin: 0px 0px 35px 0px;
  text-align: center;
  color: ${({ theme }) => theme.colors.gray06};
  font-family: ${({ theme }) => theme.fontFamily.inter};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  font-size: ${({ theme }) => theme.fontSize.small};
  line-height: 24px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    text-align: left;
  }
`

export const ProjectSectionCardsContainer = styled.div`
  position: relative;
  display: none;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    width: 100%;
    display: flex;
    justify-content: space-between;
  }
`

export const ProjectCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 255px;
  height: 295px;
  background: ${({ theme }) => theme.colors.gradients03};
  box-shadow: 0px 4px 24px -1px ${({ theme }) => theme.colors.purpleLight};
  animation: ${ShiningShadow} 3s ease-in-out infinite;
  backdrop-filter: blur(40px);
  border-radius: 8px;
`

export const ProjectCardIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 100px;
  background-color: ${({ theme }) => theme.colors.white01};
  margin-bottom: 32px;
`

export const ProjectCardTitle = styled.h1`
  margin: 0px 0px 8px 0px;
  color: ${({ theme }) => theme.colors.white01};
  font-family: ${({ theme }) => theme.fontFamily.lato};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  font-size: ${({ theme }) => theme.fontSize.p};
  line-height: 27px;
`

export const ProjectCardText = styled.p`
  max-width: 173px;
  margin: 0px 0px 8px 0px;
  text-align: center;
  color: ${({ theme }) => theme.colors.gray06};
  font-family: ${({ theme }) => theme.fontFamily.inter};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  font-size: ${({ theme }) => theme.fontSize.xxsmall};
  line-height: 21px;
`

export const DotLineProject = styled.img<ImgProps>`
  display: none;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    display: block;
    position: absolute;
    top: calc(${({ top }) => top || 0}px - 183px);
    right: ${({ right }) => right || 0}px;
    transform: rotate(${({ rotate }) => rotate || 0}deg)
      scaleX(${({ rotate }) => rotate ? -1 : 1});
  }
`

export const CustomerTestimonial = styled.div`
  width: 100%;
  background-image: url(${MobileTop}), url(${MobileBottom});
  background-repeat: no-repeat;
  background-position: top -15px right 0px, bottom 0px left -25px;
  z-index: -1;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    background-image: url(${DeskTop}), url(${DeskBottom});
    background-position: top -80px right 0px, bottom -55px left -25px;
  }
`

export const CustomerTestimonialContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  margin: 60px 0px 90px 0px;
  ${Icon} {
    width: 35px;
    margin-left: 33px;
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    flex-direction: row-reverse;
    margin: 100px 0px 300px 0px;
    ${Icon} {
      width: 45px;
      margin-left: 0px;
    }
  }
`

export const CustomerQuoteContainer = styled.div<CarouselProps>`
  position: absolute;
  animation: ${({ active }) => (active ? CarouselLeft : CarouselRight)} 2s
    linear;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    max-width: 504px;
  }
`

export const CustomerQuote = styled.p`
  margin: 23px 0px 44px 0px;
  text-align: center;
  color: ${({ theme }) => theme.colors.blueDarkerLighter};
  font-family: ${({ theme }) => theme.fontFamily.inter};
  font-weight: ${({ theme }) => theme.fontWeight.light};
  font-size: ${({ theme }) => theme.fontSize.xsmall};
  line-height: 21px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    text-align: left;
    font-size: ${({ theme }) => theme.fontSize.h6};
    line-height: 31px;
  }
`

export const CustomerInfoContainer = styled.div`
  width: 50%;
  align-self: right;
  margin: 0px 0px 0px 50%;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    margin: 0px 0px 0px 0px;
  }
`

export const CustomerName = styled.h1`
  margin: 0px 0px 8px 0px;
  text-align: left;
  color: ${({ theme }) => theme.colors.blueDarkerLight};
  font-family: ${({ theme }) => theme.fontFamily.lato};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  font-size: ${({ theme }) => theme.fontSize.h5};
`

export const RoleCompanyContainer = styled.div`
  display: flex;
  flex-direction: column;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    flex-direction: row;
  }
`

export const CustomerRole = styled.h2`
  margin: 0px 0px 6px 0px;
  text-align: left;
  color: ${({ theme }) => theme.colors.gray03};
  font-family: ${({ theme }) => theme.fontFamily.inter};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  font-size: ${({ theme }) => theme.fontSize.xxsmall};
`

export const CustomerCompany = styled.h3`
  margin: 0px;
  text-align: left;
  color: ${({ theme }) => theme.colors.blueDarkerLight};
  font-family: ${({ theme }) => theme.fontFamily.inter};
  font-weight: ${({ theme }) => theme.fontWeight.semiBold};
  font-size: ${({ theme }) => theme.fontSize.xxsmall};
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    margin-left: 15px;
  }
`

export const CarouselDotContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    justify-content: flex-start;
  }
`

export const TestimonialTitlesContainer = styled.div`
  width: 100%;
  display: flex;
  margin-top: 350px;
  @media screen and (max-width: 350px) {
    margin-top: 500px;
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    flex-direction: column;
    margin-left: 147px;
    margin-top: 100px;
  }
`

export const TestimonialTitle = styled.h1`
  display: none;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    display: flex;
    margin: 0px 0px 16px 0px;
    color: ${({ theme }) => theme.colors.gray03};
    font-family: ${({ theme }) => theme.fontFamily.inter};
    font-weight: ${({ theme }) => theme.fontWeight.medium};
    font-size: ${({ theme }) => theme.fontSize.p};
  }
`

export const TestimonialSubTitle = styled.h2`
  display: none;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    display: flex;
    margin: 0px;
    color: ${({ theme }) => theme.colors.blueDarkerDefault};
    font-family: ${({ theme }) => theme.fontFamily.lato};
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    font-size: ${({ theme }) => theme.fontSize.h4};
  }
`

export const CarouselDot = styled.div<CarouselProps>`
  margin: 64px 8px 0px 8px;
  width: 16px;
  height: 16px;
  border: ${({ active }) => (active ? 6 : 1)}px solid #74ccec;
  border-radius: 80px;
  cursor: pointer;
  :hover {
    border: 6px solid #74ccec;
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    margin: 16px 16px 0px 0px;
  }
`

export const ErrorPageWrapper = styled.div`
  position: absolute;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.white00};
  z-index: 1;
`

export const ErrorTextContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  ${Title} {
    font-family: ${({ theme }) => theme.fontFamily.lato};
    font-weight: ${({ theme }) => theme.fontWeight.extraBold};
    font-size: ${({ theme }) => theme.fontSize.h4};
  }
  ${Text} {
    font-family: ${({ theme }) => theme.fontFamily.inter};
    font-weight: ${({ theme }) => theme.fontWeight.regular};
    font-size: ${({ theme }) => theme.fontSize.xsmall};
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    align-items: flex-start;
    ${Title} {
      font-family: ${({ theme }) => theme.fontFamily.inter};
      font-weight: ${({ theme }) => theme.fontWeight.medium};
      font-size: ${({ theme }) => theme.fontSize.h1};
      margin-bottom: 32px;
    }
    ${Text} {
      max-width: 360px;
      font-size: ${({ theme }) => theme.fontSize.p};
    }
  }
`

export const ErrorIconContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 40px;
  ${Icon}:nth-child(2) {
    animation: ${GhostFloat} 10s ease-in-out infinite;
    width: 74px;
  }
  ${Icon}:nth-child(1) {
    position: absolute;
    top: 0px;
    left: 50px;
    animation: ${Shining} 2s ease-in-out infinite;
  }
  ${Icon}:nth-child(3) {
    position: absolute;
    bottom: 0px;
    right: 50px;
    animation: ${Shining} 2s ease-in-out infinite;
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    min-height: 300px;
    ${Icon}:nth-child(1) {
      width: 65px;
      left: 100px;
    }
    ${Icon}:nth-child(2) {
      width: 246px;
    }
    ${Icon}:nth-child(3) {
      width: 65px;
      right: 100px;
    }
  }
`

export const ErrorPageContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  text-align: center;
  padding: 80px 0px 0px 0px;
  ${MainButtonLink} {
    width: 224px;
    margin-top: 100px;
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    flex-direction: row-reverse;
    text-align: left;
    padding: 150px 0px 0px 0px;
  }
`

export const ErrorSocialContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 100px;
  a {
    margin: 0px 8px;
  }
`

export const ContactUsSection = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.blue800};
`

export const ContactUsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  ${TitlesContainer} {
    padding: 50px 0px 32px 0px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  ${Title} {
    font-size: ${({ theme }) => theme.fontSize.h4};
  }
  ${Text} {
    color: ${({ theme }) => theme.colors.blueDarkerDefault};
    font-family: ${({ theme }) => theme.fontFamily.inter};
    font-weight: ${({ theme }) => theme.fontWeight.regular};
    font-size: ${({ theme }) => theme.fontSize.xsmall};
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    flex-direction: row;
    justify-content: space-between;
    ${TitlesContainer} {
      width: 100%;
      align-items: flex-start;
      padding: 0px;
      margin-left: 143px;
    }
    ${Title} {
      font-size: ${({ theme }) => theme.fontSize.h2};
    }
    ${Text} {
      font-weight: ${({ theme }) => theme.fontWeight.light};
      font-size: ${({ theme }) => theme.fontSize.h5};
      line-height: 36px;
    }
  }
`

export const FormContainer = styled.div`
  width: 100%;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktopMin}px) {
    margin: 32px 0px;
  }
`
